import { useCallback, useEffect, useState } from "react";
import { document } from "@packages/global";
import { ModalDialogHeader } from "./modal-dialog-header.component";
import { ModalDialogBody } from "./modal-dialog-body.component";
import { ModalDialogFooter } from "./modal-dialog-footer.component";
import { ModalDialogProvider } from "./providers";
import { Portal } from "../../blocks";

import type { ModalDialogProps } from "./modal-dialog.types";

import styles from "./modal-dialog.module.css";
export function ModalDialog({
  children,
  className = "",
  closeOnClickOutside = true,
  closeOnEscape = true,
  id = "portal-container",
  isOpen = false,
  onClose,
  overlay = true,
  scrollLock = true,
  variant = "medium",
  ...props
}: Readonly<ModalDialogProps>) {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [renderPortal, setRenderPortal] = useState(isOpen);

  useEffect(() => {
    setIsVisible(isOpen);
    if (isOpen && scrollLock) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, scrollLock]);

  useEffect(() => {
    setTimeout(() => {
      setRenderPortal(isVisible);
    }, 300);
  }, [isVisible]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && closeOnEscape && onClose) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, closeOnEscape]);

  const handleClickOutside = useCallback(() => {
    if (closeOnClickOutside && onClose) {
      onClose();
    }
  }, [closeOnClickOutside, onClose]);

  if (!renderPortal && !isOpen) return null;

  return (
    <ModalDialogProvider onClose={onClose}>
      <Portal id={id}>
        <div
          data-overlay={overlay}
          className={`${styles.Overlay} ${
            isVisible && styles.Visible
          } ${className}`}
          onClick={handleClickOutside}
          onKeyDown={handleClickOutside}
          {...props}
        >
          <div
            className={`${styles.Container} ${styles[variant]} ${
              isVisible && styles.Visible
            }`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </Portal>
    </ModalDialogProvider>
  );
}

export namespace ModalDialog {
  export const Header = ModalDialogHeader;
  export const Body = ModalDialogBody;
  export const Footer = ModalDialogFooter;
}
